import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useReviewerAuth } from '../context/ReviewerAuthContext';
import { useTheme } from '../context/ThemeContext';
import { Sun, Moon, Eye, Star, Lock, Mail } from 'lucide-react';

export default function ReviewerLogin() {
  const { loginWithGoogle, loginWithPassword } = useReviewerAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [mode, setMode] = useState('google'); // 'google' | 'password'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGoogle = async (cr) => {
    setLoading(true); setError('');
    try {
      await loginWithGoogle(cr.credential);
      navigate('/reviewer/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Sign-in failed. You may not be registered as a reviewer.');
    } finally { setLoading(false); }
  };

  const handlePassword = async (e) => {
    e.preventDefault();
    if (!email || !password) return setError('Email and password are required.');
    setLoading(true); setError('');
    try {
      await loginWithPassword(email, password);
      navigate('/reviewer/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid email or password.');
    } finally { setLoading(false); }
  };

  return (
    <div style={s.page}>
      {/* Topbar */}
      <div style={s.topbar}>
        <div style={s.brand}>
          <img src="/logo.png" alt="Shortflix" style={{ height: 28 }}
            onError={e => { e.target.style.display = 'none'; }} />
          <span style={s.portalLabel}>Reviewer Portal</span>
        </div>
        <button onClick={toggle} style={s.themeBtn}>
          {theme === 'dark' ? <Sun size={14} color="var(--text-secondary)" /> : <Moon size={14} color="var(--text-secondary)" />}
        </button>
      </div>

      <div className="reviewer-login-container" style={s.container}>
        {/* Left */}
        <div style={s.left} className="fade-up">
          <div style={s.eyebrow}>Reviewer Access Only</div>
          <h1 style={s.headline}>
            Review films.<br />
            <span style={{ color: 'var(--pink)' }}>Shape cinema.</span>
          </h1>
          <p style={s.sub}>
            As a Shortflix reviewer, you evaluate films anonymously across seven professional categories. Your scores and feedback directly influence which films reach audiences across India.
          </p>

          <div style={s.principles}>
            {[
              [<Eye size={16} color="var(--pink)" />, 'Blind Review', 'You see only the film — never the filmmaker\'s identity or contact details'],
              [<Star size={16} color="var(--pink)" />, 'Score 7 Categories', 'Cinematography, Screenplay/Engaging, Acting, Direction, Dialogue, Music, Editing'],
              [<Lock size={16} color="var(--pink)" />, 'Anonymous Feedback', 'Your written critique is compiled anonymously for the filmmaker'],
            ].map(([icon, title, desc]) => (
              <div key={title} style={s.principle}>
                <div style={s.principleIcon}>{icon}</div>
                <div>
                  <div style={s.principleTitle}>{title}</div>
                  <div style={s.principleDesc}>{desc}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={s.notice}>
            <Lock size={12} color="var(--text-muted)" />
            <span>Access is by invitation only. Contact <strong>info@shortflixindia.com</strong> to become a reviewer.</span>
          </div>
        </div>

        {/* Right — Login card */}
        <div className="reviewer-login-right fade-up" style={s.right}>
          <div className="card" style={s.card}>
            <h2 style={s.cardTitle}>Reviewer Sign In</h2>
            <p style={s.cardSub}>Sign in to access your assigned film queue</p>

            {error && (
              <div style={s.errorBox}>{error}</div>
            )}

            {/* Mode toggle */}
            <div style={s.modeToggle}>
              <button
                onClick={() => setMode('google')}
                style={{ ...s.modeBtn, ...(mode === 'google' ? s.modeBtnActive : {}) }}>
                Google
              </button>
              <button
                onClick={() => setMode('password')}
                style={{ ...s.modeBtn, ...(mode === 'password' ? s.modeBtnActive : {}) }}>
                Email & Password
              </button>
            </div>

            {mode === 'google' ? (
              <div style={{ marginBottom: 8 }}>
                {loading
                  ? <div style={s.loadingBtn}>Signing in...</div>
                  : <GoogleLogin onSuccess={handleGoogle} onError={() => setError('Google sign-in failed.')}
                      theme="filled_black" size="large" width="100%" text="continue_with" shape="rectangular" />
                }
                <p style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', marginTop: 12, fontWeight: 400 }}>
                  Your Google account must match your registered reviewer email.
                </p>
              </div>
            ) : (
              <form onSubmit={handlePassword}>
                <div style={{ marginBottom: 14 }}>
                  <label style={s.label}>Email</label>
                  <div style={{ position: 'relative' }}>
                    <Mail size={14} color="var(--text-muted)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
                    <input
                      type="email" value={email} onChange={e => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      style={{ paddingLeft: 36 }}
                    />
                  </div>
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={s.label}>Password</label>
                  <div style={{ position: 'relative' }}>
                    <Lock size={14} color="var(--text-muted)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
                    <input
                      type="password" value={password} onChange={e => setPassword(e.target.value)}
                      placeholder="••••••••"
                      style={{ paddingLeft: 36 }}
                    />
                  </div>
                </div>
                <button type="submit" disabled={loading} className="btn-primary" style={{ width: '100%', padding: '12px' }}>
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' },
  topbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 32px', borderBottom: '1.5px solid var(--border)' },
  brand: { display: 'flex', alignItems: 'center', gap: 12 },
  portalLabel: { fontSize: 10, fontWeight: 800, color: 'var(--text-muted)', letterSpacing: '0.12em', textTransform: 'uppercase', background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', borderRadius: 20, padding: '3px 10px' },
  themeBtn: { width: 32, height: 32, borderRadius: 'var(--r-sm)', background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  container: { flex: 1, display: 'flex', maxWidth: 1100, margin: '0 auto', width: '100%', padding: '64px 32px', gap: 80, alignItems: 'center' },
  left: { flex: 1 },
  eyebrow: { fontSize: 10, fontWeight: 800, color: 'var(--pink)', letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 16 },
  headline: { fontWeight: 900, fontSize: 50, lineHeight: 1.1, marginBottom: 20, letterSpacing: '-0.02em', color: 'var(--text)' },
  sub: { color: 'var(--text-secondary)', fontSize: 15, lineHeight: 1.7, marginBottom: 32, maxWidth: 420, fontWeight: 400 },
  principles: { display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 28 },
  principle: { display: 'flex', alignItems: 'flex-start', gap: 12 },
  principleIcon: { width: 36, height: 36, background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  principleTitle: { fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 2 },
  principleDesc: { fontSize: 12, color: 'var(--text-muted)', fontWeight: 400 },
  notice: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-muted)', fontWeight: 400 },
  right: { width: 380, flexShrink: 0 },
  card: { padding: 30 },
  cardTitle: { fontWeight: 900, fontSize: 22, color: 'var(--text)', marginBottom: 6, letterSpacing: '-0.01em' },
  cardSub: { color: 'var(--text-secondary)', fontSize: 13, marginBottom: 22, fontWeight: 400 },
  errorBox: { background: 'rgba(255,68,68,0.08)', border: '1.5px solid rgba(255,68,68,0.25)', color: 'var(--red)', borderRadius: 'var(--r-md)', padding: '10px 14px', fontSize: 13, marginBottom: 16, fontWeight: 500 },
  modeToggle: { display: 'flex', background: 'var(--bg-elevated)', borderRadius: 'var(--r-md)', padding: 3, marginBottom: 20 },
  modeBtn: { flex: 1, padding: '8px', borderRadius: 'var(--r-sm)', fontSize: 12, fontWeight: 700, background: 'none', color: 'var(--text-muted)', border: 'none', cursor: 'pointer', fontFamily: 'var(--font)', transition: 'all 0.2s', textTransform: 'uppercase', letterSpacing: '0.06em' },
  modeBtnActive: { background: 'var(--bg-card)', color: 'var(--pink)', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  loadingBtn: { background: 'var(--bg-elevated)', color: 'var(--text-muted)', padding: 12, borderRadius: 'var(--r-md)', textAlign: 'center', fontSize: 13 },
  label: { display: 'block', fontSize: 10, fontWeight: 800, color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em' },
};
